import React from 'react';
import { motion } from 'framer-motion';
import { Code, TrendingUp, Wrench, Calculator, Building, HeartPulse, Megaphone, Factory, Users, Zap, Network } from 'lucide-react';
import { niches } from '../data/mock';
import { Reveal, SectionHeading } from './Reveal';

const icons = { code: Code, calculator: Calculator, trending: TrendingUp, wrench: Wrench, building: Building, heart: HeartPulse, megaphone: Megaphone, factory: Factory };

const points = [
  { icon: Users, title: 'Ausschließlich freiberufliche Recruiting-Profis', text: 'Keine festangestellten Generalisten. Wir arbeiten nur mit selbstständigen Recruitern, die seit Jahren in genau einer Branche vermitteln.' },
  { icon: Network, title: 'Für jede Position der passende Spezialist', text: 'Deine Vakanz landet nicht bei „irgendwem“, sondern bei dem Profi, der dein Fachgebiet, dessen Gehälter und dessen Kandidaten kennt.' },
  { icon: Zap, title: 'Maximale Power – in jeder Nische', text: 'Dadurch sind wir in IT genauso stark wie in Finance, Handwerk oder Healthcare. Ohne Qualitätsverlust, ohne Einarbeitung.' },
];

const NicheOrbit = () => (
  <div className="relative aspect-square max-w-[540px] mx-auto" data-testid="niche-orbit">
    <div className="absolute inset-0 rounded-full bg-violet-100/60 blur-[80px]" />
    <motion.div
      className="absolute inset-[8%] rounded-full border border-dashed border-violet-300/70"
      animate={{ rotate: 360 }}
      transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
    />
    <div className="absolute inset-[26%] rounded-full border border-stone-200" />
    <motion.div
      className="absolute inset-[8%] rounded-full"
      animate={{ rotate: -360 }}
      transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
    >
      <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-amber-400 shadow-[0_0_12px_rgba(245,158,11,0.8)]" />
    </motion.div>

    <div className="absolute inset-0 flex items-center justify-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ type: 'spring', stiffness: 120, damping: 16 }}
        className="relative w-[32%] aspect-square rounded-full bg-obsidian flex flex-col items-center justify-center text-center shadow-2xl shadow-violet-900/40 border border-white/10"
      >
        <div className="absolute inset-0 rounded-full animate-ping bg-violet-500/10" />
        <img src="/hireka-logo.png" alt="HIREKA" className="relative h-5 sm:h-6 brightness-0 invert" />
        <p className="relative mt-1.5 text-[10px] sm:text-xs text-amber-300 font-semibold tracking-[0.15em] uppercase">Dein Briefing</p>
      </motion.div>
    </div>

    {niches.map((n, i) => {
      const Icon = icons[n.icon];
      const angle = (i / niches.length) * Math.PI * 2 - Math.PI / 2;
      const r = 42;
      const x = 50 + r * Math.cos(angle);
      const y = 50 + r * Math.sin(angle);
      return (
        <React.Fragment key={n.name}>
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100">
            <motion.line
              x1="50" y1="50" x2={x} y2={y}
              stroke="url(#lineGrad)" strokeWidth="0.35"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 + i * 0.08 }}
            />
            <defs>
              <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#7C3AED" />
                <stop offset="100%" stopColor="#F59E0B" />
              </linearGradient>
            </defs>
          </svg>
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: 'spring', stiffness: 140, damping: 14, delay: 0.4 + i * 0.08 }}
            className="absolute group"
            style={{ left: `${x}%`, top: `${y}%`, x: '-50%', y: '-50%' }}
            data-testid={`niche-node-${i}`}
          >
            <div className="lux-card w-[76px] sm:w-[104px] px-2 py-2.5 sm:py-3 text-center !rounded-2xl">
              <div className="mx-auto w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center group-hover:bg-amber-50 group-hover:border-amber-200 transition-colors">
                <Icon className="w-4 h-4 text-violet-700 group-hover:text-amber-600 transition-colors" />
              </div>
              <p className="mt-1.5 text-[11px] sm:text-xs font-bold text-slate-900 leading-tight">{n.name}</p>
              <p className="hidden sm:block text-[10px] text-slate-500 leading-tight mt-0.5">{n.roles}</p>
            </div>
          </motion.div>
        </React.Fragment>
      );
    })}
  </div>
);

const ExpertsSection = () => (
  <section id="experten" className="relative bg-white py-24 lg:py-32 overflow-hidden" data-testid="experts-section">
    <div className="absolute right-0 bottom-0 w-[600px] h-[600px] rounded-full bg-amber-100/50 blur-[140px] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-16 items-center">
      <div className="lg:col-span-6">
        <SectionHeading
          align="left"
          eyebrow="Unser Netzwerk"
          title={<>Für jede Nische <span className="text-violet-gradient">maximale Power.</span></>}
          subtitle="So funktioniert Recruiting bei HIREKA: Dein Briefing geht an den freiberuflichen Recruiting-Profi, der genau deine Branche kennt – nicht an einen Generalisten."
        />
        <div className="mt-10 space-y-6">
          {points.map((p, i) => (
            <Reveal key={i} delay={0.1 + i * 0.1} className="flex gap-5 items-start" data-testid={`niche-point-${i}`}>
              <div className="shrink-0 w-12 h-12 rounded-2xl bg-obsidian flex items-center justify-center shadow-lg shadow-violet-900/20">
                <p.icon className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-slate-900">{p.title}</h3>
                <p className="mt-1.5 text-slate-600 leading-relaxed text-sm md:text-base">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <div className="lg:col-span-6">
        <NicheOrbit />
        <Reveal delay={0.3} className="mt-8 text-center text-sm text-slate-500">
          Egal ob IT, Finance, Handwerk oder Healthcare – <span className="text-slate-900 font-semibold">ein Festpreis, ein Prozess, der jeweils beste Spezialist.</span>
        </Reveal>
      </div>
    </div>
  </section>
);

export default ExpertsSection;
